import React from 'react';

/**
 * StatusPill — pill de status com ponto colorido (fonte-da-verdade, findings F47/F62-F65).
 *
 * Visual vencedor (família B, 31 arquivos): SEM borda, altura fixa 23px,
 * dot `currentColor` (sempre bate com a cor do texto). Cores exclusivamente
 * via tokens `var(--theme-*)` — NUNCA hex hardcoded (F63).
 *
 * Semântica de cor (decisões do dono):
 * - verde  = ligado/sucesso ("Ativo" liga/desliga, "Concluído" — F64/F65)
 * - azul   = em andamento ("Em atendimento", "Em execução" — F64)
 * - âmbar  = atenção ("Pendente")
 * - vermelho = falha ("Erro", "Falhou")
 * - neutro = desligado/sem estado ("Inativo", "Rascunho")
 *
 * Para status ligável/desligável em listagens, o padrão é o Switch shared (sm)
 * — pill só para status NÃO toggláveis (decisão F47).
 */

export type StatusPillVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

/* Cores por semântica — mesmos pares subtle/fg já usados pela família B
   (ex.: UserListItem, LevelListItem); neutral = surface-sunken + muted-text. */
const VARIANT_STYLES: Record<StatusPillVariant, React.CSSProperties> = {
  success: { backgroundColor: 'var(--theme-success-subtle)', color: 'var(--theme-success-fg)' },
  warning: { backgroundColor: 'var(--theme-warning-subtle)', color: 'var(--theme-warning-fg)' },
  error: { backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' },
  info: { backgroundColor: 'var(--theme-info-subtle)', color: 'var(--theme-info-fg)' },
  neutral: { backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' },
};

/**
 * Mapa semântico status → variant (centraliza as decisões F64/F65).
 * Usar na Fase 2 no lugar dos mapas locais de cada módulo.
 */
export const STATUS_SEMANTICS = {
  active: 'success', // "Ativo" liga/desliga = verde universal (F64)
  inactive: 'neutral',
  draft: 'neutral',
  pending: 'warning',
  scheduled: 'info',
  running: 'info',
  in_progress: 'info', // azul = em andamento (decisão F64)
  in_service: 'info', // conversa ativa → rótulo "Em atendimento", azul (F64)
  completed: 'success', // "Concluído" = verde, inclusive em Projetos (F65)
  error: 'error',
  failed: 'error',
  cancelled: 'error',
  overdue: 'error',
  connected: 'success',
  disconnected: 'error',
  paused: 'neutral',
  archived: 'neutral',
} as const satisfies Record<string, StatusPillVariant>;

export type StatusSemanticKey = keyof typeof STATUS_SEMANTICS;

/** Resolve o variant de um status arbitrário (fallback: neutral). */
export function getStatusVariant(status: string): StatusPillVariant {
  return (STATUS_SEMANTICS as Record<string, StatusPillVariant>)[status] ?? 'neutral';
}

export interface StatusPillProps {
  /** Semântica de cor do pill */
  variant: StatusPillVariant;
  /** Texto exibido (rótulo pt-BR é responsabilidade do consumidor) */
  label: string;
  /** Ponto colorido (currentColor) à esquerda */
  dot?: boolean;
  /** Classes extras (margens/posicionamento) */
  className?: string;
  'data-testid'?: string;
}

/**
 * @example
 * ```tsx
 * <StatusPill variant={isActive ? 'success' : 'neutral'} label={isActive ? 'Ativo' : 'Inativo'} />
 * <StatusPill variant={getStatusVariant(campaign.status)} label={statusLabel} data-testid={`campaign-status-${campaign.id}`} />
 * ```
 */
export const StatusPill: React.FC<StatusPillProps> = ({
  variant,
  label,
  dot = true,
  className,
  'data-testid': dataTestId,
}) => {
  return (
    <span
      data-testid={dataTestId}
      className={`inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold whitespace-nowrap ${className ?? ''}`}
      style={VARIANT_STYLES[variant]}
    >
      {dot && (
        <span
          className="w-1.5 h-1.5 rounded-full flex-none"
          style={{ backgroundColor: 'currentColor' }}
        />
      )}
      {label}
    </span>
  );
};

StatusPill.displayName = 'StatusPill';
